import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Megaphone, UtensilsCrossed, HandHeart, ArrowRight, Play, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import API from "@/apiConfig";

const AUTO_REDIRECT_SECONDS = 10;

export default function CollectiveWelcomePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [status, setStatus] = useState("loading"); // loading | valid | error
  const [collective, setCollective] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [countdown, setCountdown] = useState(AUTO_REDIRECT_SECONDS);
  const timerRef = useRef(null);

  // Verify the token on mount
  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMsg("No invite token provided.");
      return;
    }
    axios
      .get(`${API}/collectives/${slug}/verify-invite?token=${encodeURIComponent(token)}`)
      .then((r) => {
        setCollective(r.data.collective);
        setStatus("valid");
        toast.success(`Shalom! Welcome to ${r.data.collective.display_name}`);
      })
      .catch((err) => {
        const msg = err.response?.data?.detail || "This invite link is invalid or has expired.";
        setStatus("error");
        setErrorMsg(msg);
      });
  }, [slug, token]);

  // Auto-redirect countdown when token is valid
  useEffect(() => {
    if (status !== "valid") return;
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          navigate(`/collectives/${slug}/room`);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [status, slug, navigate]);

  const goToRoom = (tab) => {
    clearInterval(timerRef.current);
    navigate(`/collectives/${slug}/room${tab ? `?tab=${tab}` : ""}`);
  };

  // ── Loading state ──
  if (status === "loading") {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center" data-testid="welcome-loading">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-3 border-navy/20 border-t-gold rounded-full animate-spin" />
          <span className="text-sm text-navy/60 font-body">Verifying your invite...</span>
        </div>
      </div>
    );
  }

  // ── Error state ──
  if (status === "error") {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center px-4" data-testid="welcome-error">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <span className="text-red-500 text-2xl font-heading">!</span>
          </div>
          <h1 className="font-heading text-2xl text-navy mb-2">Link Unavailable</h1>
          <p className="text-gray-500 text-sm mb-6 leading-relaxed">{errorMsg}</p>
          <Button
            data-testid="welcome-error-home-btn"
            onClick={() => navigate("/collectives")}
            className="bg-navy text-bone hover:bg-navy/90"
          >
            Browse Collectives
          </Button>
        </div>
      </div>
    );
  }

  // ── Valid token — Welcome page ──
  return (
    <div className="min-h-screen bg-bone" data-testid="welcome-page">
      {/* Hero */}
      <div className="bg-navy text-bone px-4 pt-10 pb-8">
        <div className="max-w-lg mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-gold/15 text-gold text-xs font-semibold tracking-wide uppercase px-3 py-1 rounded-full mb-4">
            <CheckCircle className="w-3.5 h-3.5" />
            You're Invited
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl mb-2" data-testid="welcome-title">
            Welcome to {collective?.display_name}
          </h1>
          <p className="text-bone/70 text-sm leading-relaxed max-w-sm mx-auto">
            {collective?.description || "Your Collective family is waiting. Explore what's been built for you."}
          </p>
          {collective?.meeting_schedule && (
            <p className="text-gold text-xs mt-3 font-medium">{collective.meeting_schedule}</p>
          )}
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 -mt-4 pb-12">
        {/* Video placeholder */}
        <div
          className="bg-navy/5 border border-navy/10 rounded-xl overflow-hidden mb-8 aspect-video flex items-center justify-center relative"
          data-testid="welcome-video-placeholder"
        >
          <div className="text-center">
            <div className="w-14 h-14 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-3">
              <Play className="w-6 h-6 text-gold ml-0.5" />
            </div>
            <p className="text-navy/50 text-sm font-medium">Welcome Video</p>
            <p className="text-navy/35 text-xs mt-1">Coming soon — a personal word from your Guide</p>
          </div>
        </div>

        {/* Three action buttons */}
        <h2 className="font-heading text-lg text-navy mb-4 text-center">Get Started</h2>
        <div className="flex flex-col gap-3">
          <button
            data-testid="welcome-btn-announcements"
            onClick={() => goToRoom("announcements")}
            className="group flex items-center gap-4 w-full bg-white border border-navy/10 rounded-xl px-5 py-4 text-left hover:border-gold/40 hover:shadow-sm transition-all"
          >
            <div className="w-11 h-11 rounded-lg bg-gold/10 flex items-center justify-center shrink-0 group-hover:bg-gold/20 transition-colors">
              <Megaphone className="w-5 h-5 text-gold" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-navy text-sm">See Announcements</p>
              <p className="text-navy/45 text-xs mt-0.5">Latest updates from your Guide Pair</p>
            </div>
            <ArrowRight className="w-4 h-4 text-navy/25 group-hover:text-gold transition-colors shrink-0" />
          </button>

          <button
            data-testid="welcome-btn-meals"
            onClick={() => goToRoom("meals")}
            className="group flex items-center gap-4 w-full bg-white border border-navy/10 rounded-xl px-5 py-4 text-left hover:border-gold/40 hover:shadow-sm transition-all"
          >
            <div className="w-11 h-11 rounded-lg bg-gold/10 flex items-center justify-center shrink-0 group-hover:bg-gold/20 transition-colors">
              <UtensilsCrossed className="w-5 h-5 text-gold" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-navy text-sm">Plan Next Meal</p>
              <p className="text-navy/45 text-xs mt-0.5">Claim a dish slot for fellowship</p>
            </div>
            <ArrowRight className="w-4 h-4 text-navy/25 group-hover:text-gold transition-colors shrink-0" />
          </button>

          <button
            data-testid="welcome-btn-prayer"
            onClick={() => goToRoom("prayer")}
            className="group flex items-center gap-4 w-full bg-white border border-navy/10 rounded-xl px-5 py-4 text-left hover:border-gold/40 hover:shadow-sm transition-all"
          >
            <div className="w-11 h-11 rounded-lg bg-gold/10 flex items-center justify-center shrink-0 group-hover:bg-gold/20 transition-colors">
              <HandHeart className="w-5 h-5 text-gold" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-navy text-sm">Post Prayer Request</p>
              <p className="text-navy/45 text-xs mt-0.5">Let your Collective lift you up</p>
            </div>
            <ArrowRight className="w-4 h-4 text-navy/25 group-hover:text-gold transition-colors shrink-0" />
          </button>
        </div>

        {/* Auto-redirect notice */}
        <p className="text-center text-navy/35 text-xs mt-8">
          Entering your Collective room in {countdown}s...{" "}
          <button
            data-testid="welcome-enter-now-btn"
            onClick={() => goToRoom()}
            className="text-gold underline underline-offset-2 hover:text-gold/80"
          >
            Enter now
          </button>
        </p>
      </div>
    </div>
  );
}
